import { useState, useEffect, useCallback } from 'react';
import {
  getReferrerNames,
  createReferrerName,
  updateReferrerName,
  deleteReferrerName,
} from '../services/ReferrerNameService';
import { ReferrerName } from '../types/interfaces';

// Referencia a la petición en curso para evitar doble GET en desarrollo (React StrictMode
// ejecuta los efectos dos veces) o cuando varios componentes usan el hook a la vez.
let referrerNamesRequest: Promise<{ success?: boolean; data?: ReferrerName[]; message?: string }> | null = null;

export const useReferrerNames = () => {
  const [referrerNames, setReferrerNames] = useState<ReferrerName[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const fetchReferrerNames = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && referrerNamesRequest) {
      try {
        const response = await referrerNamesRequest;
        if (response.success && response.data) setReferrerNames(response.data);
        else if (response.message) setError(response.message);
      } catch {
        setError('Error al cargar los referidos');
      } finally {
        setIsLoading(false);
      }
      return;
    }
    referrerNamesRequest = getReferrerNames();
    setIsLoading(true);
    setError(null);
    try {
      console.log('[useReferrerNames] Fetching referrer names...');
      const response = await referrerNamesRequest;
      if (response.success && response.data) {
        setReferrerNames(response.data);
        console.log('[useReferrerNames] Successfully loaded referrer names:', response.data.length);
      } else {
        const errorMsg = response.message || 'Error al cargar los referidos';
        setError(errorMsg);
        console.error('[useReferrerNames] Error loading:', errorMsg);
      }
    } catch (err) {
      const errorMsg = 'Error al cargar los referidos';
      setError(errorMsg);
      console.error('[useReferrerNames] Exception:', err);
    } finally {
      referrerNamesRequest = null;
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferrerNames();
  }, [fetchReferrerNames]);

  const refreshReferrerNames = useCallback(() => {
    console.log('[useReferrerNames] Refreshing referrer names...');
    return fetchReferrerNames(true);
  }, [fetchReferrerNames]);

  const createReferrer = useCallback(
    async (name: string, code: string): Promise<{ success: boolean; message?: string }> => {
      setIsCreating(true);
      setError(null);
      try {
        console.log('[useReferrerNames] Creating referrer:', { name, code });
        const response = await createReferrerName(name, code);
        if (response.success) {
          // Refrescar la lista después de crear
          await refreshReferrerNames();
          console.log('[useReferrerNames] Successfully created referrer');
          return { success: true };
        } else {
          const errorMsg = response.message || 'Error al crear el referido';
          setError(errorMsg);
          console.error('[useReferrerNames] Error creating:', errorMsg);
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        const errorMsg = 'Error al crear el referido';
        setError(errorMsg);
        console.error('[useReferrerNames] Exception creating:', err);
        return { success: false, message: errorMsg };
      } finally {
        setIsCreating(false);
      }
    },
    [refreshReferrerNames]
  );

  const updateReferrer = useCallback(
    async (
      id: number,
      name: string,
      code: string
    ): Promise<{ success: boolean; message?: string }> => {
      setIsUpdating(true);
      setError(null);
      try {
        console.log('[useReferrerNames] Updating referrer:', { id, name, code });
        const response = await updateReferrerName(id, name, code);
        if (response.success) {
          // Refrescar la lista después de actualizar
          await refreshReferrerNames();
          console.log('[useReferrerNames] Successfully updated referrer');
          return { success: true };
        } else {
          const errorMsg = response.message || 'Error al actualizar el referido';
          setError(errorMsg);
          console.error('[useReferrerNames] Error updating:', errorMsg);
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        const errorMsg = 'Error al actualizar el referido';
        setError(errorMsg);
        console.error('[useReferrerNames] Exception updating:', err);
        return { success: false, message: errorMsg };
      } finally {
        setIsUpdating(false);
      }
    },
    [refreshReferrerNames]
  );

  const removeReferrer = useCallback(
    async (id: number): Promise<{ success: boolean; message?: string }> => {
      setIsDeleting(true);
      setError(null);
      try {
        console.log('[useReferrerNames] Deleting referrer:', id);
        const response = await deleteReferrerName(id);
        if (response.success) {
          // Refrescar la lista después de eliminar
          await refreshReferrerNames();
          console.log('[useReferrerNames] Successfully deleted referrer');
          return { success: true };
        } else {
          const errorMsg = response.message || 'Error al eliminar el referido';
          setError(errorMsg);
          console.error('[useReferrerNames] Error deleting:', errorMsg);
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        const errorMsg = 'Error al eliminar el referido';
        setError(errorMsg);
        console.error('[useReferrerNames] Exception deleting:', err);
        return { success: false, message: errorMsg };
      } finally {
        setIsDeleting(false);
      }
    },
    [refreshReferrerNames]
  );

  return {
    referrerNames,
    isLoading,
    error,
    isCreating,
    isUpdating,
    isDeleting,
    refreshReferrerNames,
    createReferrer,
    updateReferrer,
    removeReferrer,
  };
};

